import type { NavigationGuardNext, RouteLocationNormalized, RouteRecordRaw } from 'vue-router'
import type { AxiosError } from 'axios'
import { useUtmStore } from '@/stores/utm'
import { useQuoteStore } from '@/stores/quote'
import { StateEnum } from '@apiTypes'
import type { QuoteByHash } from '@apiTypes'
import { useAuthStore } from '@/stores/auth'
import { useWorkspaceStore } from '@/stores/workspace'
import { clearAuthData, hasRefreshToken, isUserLoggedIn, refreshTokenHelper } from '@/auth/utils'
import { publicPages } from '@/router/public-pages'
import { usePriceStore } from '@/stores/price'

type RouteGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => RouteGuardResponse
type RouteGuardResponse = Promise<any>

export const routeGuardMap: Record<string, RouteGuard> = {
  'free-quote': waypointsCheckGuard,
  'thanks': waypointsCheckGuard,
  'on-the-way': waypointsCheckGuard,
  'magic-link-hash': quoteByHashGuard,
  'login': loggedUserRedirectGuard,
  'register': loggedUserRedirectGuard,
  'index': landingDataGuard,
  'order-hash': orderGuard,
  'workspace-orders': ordersAccessGuard,
  'workspace-invoices': workspacePermissionGuard('can_access_invoices'),
  'workspace-company-details': workspacePermissionGuard('can_access_company_details'),
  'workspace-loyalty-program': loyaltyGuard,
  'workspace-loyalty-orders': loyaltyGuard,
}

export function extendRouteWithGuard(route: RouteRecordRaw): RouteRecordRaw {
  const routeName = route.children?.[0]?.name?.toString() || ''
  const guards: RouteGuard[] = []

  if (!publicPages.includes(routeName))
    guards.push(dataGuard)

  if (routeGuardMap[routeName])
    guards.push(routeGuardMap[routeName])

  return { ...route, beforeEnter: guards }
}

async function dataGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): RouteGuardResponse {
  // Cheap no-op once resolved. Deliberately not awaited: it must never delay a
  // navigation, but it does give a failed boot fetch a retry on every move
  // through the app — otherwise the only code path that retries sits behind
  // the navbar entry that the failure itself hid.
  usePriceStore().ensureSettingsLoaded()

  if (isUserLoggedIn()) {
    try {
      await fetchData()
      next()
    }
    catch (e: unknown) {
      await checkGuardDataError(e as AxiosError, next, undefined, 'login')
    }
  }
  else {
    next({ name: 'login' })
  }
}

async function waypointsCheckGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): RouteGuardResponse {
  const store = useQuoteStore()
  const utmStore = useUtmStore()

  if (!store?.quoteByID?.waypoints || store?.quoteByID?.waypoints.length < 2) { next('/') }
  else {
    if (store.quoteByID.uuid && to.path.includes('free-quote') && utmStore.utm.utm_source)
      await store.updateUtmParams(utmStore.utm)

    next()
  }
}

async function quoteByHashGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): RouteGuardResponse {
  const hash = to.params.hash as string
  const store = useQuoteStore()

  await ensureUserData()

  try {
    if (canReuseQuoteByHash(store.getQuoteByHash, hash)) {
      next()

      return
    }

    const quote = await store.fetchQuoteByHash(hash)

    if (quote && quote.state !== StateEnum.CANCELED) {
      if (quote.state === StateEnum.CONFIRMED || quote.state === StateEnum.WITH_ORDER)
        next({ name: 'order-hash', params: { hash } })
      else
        next()
    }
    else { next({ path: '/', query: { canceled: 'true' } }) }
  }
  catch (e: unknown) {
    await checkGuardDataError(e as AxiosError, next, 'login')
  }
}

// Re-entering the magic link of the quote already in the store (e.g. with the
// back button) shows it without a round-trip. A copy without offers cannot be
// shown, though: /free-quote/<hash> loads the quote before "Get your price"
// prices it, and the order page's payload carries no offers at all. Only a
// confirmed quote, or one that has its order by now, needs none: the magic
// link just shows its confirmation. Loading it again would send the back
// button from the order page to the order page, a redirect the router drops,
// leaving the order page under the magic link's URL.
function canReuseQuoteByHash(quote: Partial<QuoteByHash> | undefined, hash: string): boolean {
  if (quote?.hash !== hash)
    return false

  return quote.state === StateEnum.CONFIRMED
    || quote.state === StateEnum.WITH_ORDER
    || Boolean(quote.price_options?.length)
}

async function orderGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): RouteGuardResponse {
  const hash = to.params.hash as string
  const store = useQuoteStore()

  await ensureUserData()

  try {
    const quote = await store.fetchQuoteWithOrder(hash)

    if (quote.state === StateEnum.CONFIRMED || quote.state === StateEnum.WITH_ORDER) {
      next()

      return
    }

    if (quote.state === StateEnum.PRICEABLE)
      next({ name: 'magic-link-hash', params: { hash } })
    else
      next('/')
  }
  catch (e: unknown) {
    next('/')
  }
}

export async function loggedUserRedirectGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): RouteGuardResponse {
  const authStore = useAuthStore()

  if (isUserLoggedIn()) {
    try {
      await authStore.fetchCurrentUser()
      next({ name: 'index' })
    }
    catch (e: unknown) {
      await checkGuardDataError(e as AxiosError, next, 'index')
    }
  }
  else {
    next()
  }
}

// The tokens are shared by every tab: they are cleared only when the session is
// really over (the refresh was refused, or another tab signed out), not after a
// server error that the next request may not have.
function isSessionOver(error: unknown) {
  return (error as AxiosError)?.response?.status === 401 || !hasRefreshToken()
}

async function checkGuardDataError(error: AxiosError, next: NavigationGuardNext, redirectName?: string, unauthorizedRedirectName?: string) {
  if (error.response?.status === 401) {
    try {
      await refreshTokenHelper(error.config)
      await fetchData()
      redirectName ? next({ name: redirectName }) : next()
    }
    catch (e: unknown) {
      if (isSessionOver(e))
        clearAuthData()

      // if there is error during token refresh we should redirect to login or register page based on user navigation
      unauthorizedRedirectName ? next({ name: unauthorizedRedirectName }) : next()
    }
  }
  else {
    next({ name: 'error' })
  }
}

async function landingDataGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): RouteGuardResponse {
  const utmStore = useUtmStore()

  utmStore.storeUtmParameters(to.query)

  if (isUserLoggedIn()) {
    try {
      await fetchData()
      next()
    }
    catch (e: unknown) {
      await checkGuardDataError(e as AxiosError, next)
    }
  }
  else {
    next()
  }
}

type WorkspacePermissionKey =
  | 'can_access_invoices'
  | 'can_access_company_details'
  | 'can_access_loyalty'

// Gate workspace sub-pages that require both a verified Swida-linked
// workspace AND an active permission flag. Users hitting these URLs
// directly (or refreshing a stale bookmark) bounce to /workspace/orders
// which is always allowed.
function workspacePermissionGuard(permission: WorkspacePermissionKey): RouteGuard {
  return async function permissionGuard(to, from, next) {
    const workspaceStore = useWorkspaceStore()

    if (!workspaceStore.activeWorkspace)
      await workspaceStore.fetchActiveWorkspace()

    const ws = workspaceStore.activeWorkspace
    const hasSwidaLink = Boolean(ws?.is_verified && ws?.company_id)
    const hasPermission = Boolean(ws?.current_user_permissions?.[permission])

    if (hasSwidaLink && hasPermission)
      next()
    else
      next({ path: '/workspace/orders' })
  }
}

// Loyalty hangs off the `Feature flags` singleton in Django admin. While it is
// off the pages must be unreachable by direct URL or stale bookmark too, so we
// land on /workspace/orders — the same place an unpermitted user already ends
// up, which keeps the redirect indistinguishable from the pre-existing
// permission bounce.
//
// The flags are awaited rather than read optimistically: on a cold load
// straight into /workspace/loyalty-program the request is still in flight, and
// the "off" default would bounce a user who should be let through.
//
// If that request *fails* we bounce anyway — deliberately. A kill switch we
// cannot confirm is on has to read as off, and the API refuses the loyalty
// endpoints regardless, so letting the page through during an outage would
// only render a killed feature full of error banners. `loaded` stays false
// after a failure, so the next navigation retries instead of pinning the
// session.
//
// Declared as a function (not a const) so routeGuardMap above, which is
// evaluated at module load, can reference it without hitting the TDZ.
async function loyaltyGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): RouteGuardResponse {
  const priceStore = usePriceStore()
  const workspaceStore = useWorkspaceStore()

  // Independent lookups: a cold deep-link would otherwise pay for the settings
  // round-trip and the workspace round-trip back to back.
  const [settings] = await Promise.all([
    priceStore.ensureSettingsLoaded(),
    workspaceStore.activeWorkspace
      ? Promise.resolve(null)
      : workspaceStore.fetchActiveWorkspace().catch(() => null),
  ])

  if (settings.loyalty_program_enabled !== true)
    return next({ path: '/workspace/orders' })

  return workspacePermissionGuard('can_access_loyalty')(to, from, next)
}

// Unverified company drafts have no Orders tab — nothing is linked to
// Swida yet. The personal workspace always keeps Orders (local IC data).
// Direct hits / stale bookmarks from an unverified company bounce home.
async function ordersAccessGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): RouteGuardResponse {
  const workspaceStore = useWorkspaceStore()

  if (!workspaceStore.activeWorkspace)
    await workspaceStore.fetchActiveWorkspace()

  const ws = workspaceStore.activeWorkspace
  const isPersonalWorkspace = ws?.workspace_type === 'personal'
  const hasSwidaLink = Boolean(ws?.is_verified && ws?.company_id)

  if (ws && (isPersonalWorkspace || hasSwidaLink))
    next()
  else
    next({ path: '/' })
}

async function tryRefreshAndFetchData(failedRequest?: AxiosError['config']) {
  try {
    await refreshTokenHelper(failedRequest)
    await fetchData()
  }
  catch (e: unknown) {
    if (isSessionOver(e))
      clearAuthData()
  }
}

async function ensureUserData() {
  if (!isUserLoggedIn())
    return

  try {
    await fetchData()
  }
  catch (e: unknown) {
    const error = e as AxiosError
    if (error.response?.status === 401)
      await tryRefreshAndFetchData(error.config)
  }
}

async function fetchData() {
  const authStore = useAuthStore()
  if (!authStore.currentUser)
    await authStore.fetchCurrentUser()
}
